<template>
  <!-- Modal chọn gói cước cho khu vực dạng Transfer List 2 cột -->
  <b-modal
    :id="modalId"
    :title="`${$t('subscriptionPlan')} (${totalPackage})`"
    dialog-class="region-plan-modal-dialog"
    hide-footer
    no-close-on-backdrop
    @hide="onModalHide"
  >
    <div>
      <!-- Nút đóng góc trên bên phải -->
      <a
        type="button"
        @click="onClickCancel"
        aria-label="Close"
        class="close"
        style="position: absolute; top: -47px; right: 11px; width: 18px; background-color: #fff; z-index: 9999;"
      >×</a>

      <b-row style="overflow-y: hidden; margin-top: 10px;">
        <!-- Cột trái: Tìm kiếm, chọn/bỏ chọn tất cả và danh sách gói cước khả dụng -->
        <b-col cols="6">
          <b-form class="mb-3 text-nowrap" @submit.stop.prevent="searchSubscriptionPlans">
            <div class="d-flex align-items-center">
              <b-input-group class="flex-grow-1">
                <b-form-input
                  v-model="searchKeyword"
                  :placeholder="$t('search')"
                  minlength="2"
                  maxlength="150"
                  @input="onSearchInput"
                  @keydown.enter.stop.prevent="searchSubscriptionPlans"
                />
                <b-input-group-append>
                  <b-button type="button" variant="outline-primary" @click="searchSubscriptionPlans">
                    <b-icon icon="search" />
                  </b-button>
                </b-input-group-append>
              </b-input-group>

              <!-- Các nút chọn tất cả / bỏ chọn tất cả nhanh -->
              <div class="ml-2 d-flex">
                <b-button @click="onClickSelectAll" variant="outline-primary" class="mr-2">
                  {{ $t("selectAll") }}
                </b-button>
                <b-button @click="onClickdeselectAll" variant="outline-primary">
                  {{ $t("deselectAll") }}
                </b-button>
              </div>
            </div>
          </b-form>

          <!-- Danh sách gói cước khả dụng có thể chọn -->
          <div class="text-left" style="height: 450px !important; overflow-y: hidden;">
            <div style="height: 100% !important; overflow-y: scroll; min-height: 100% !important; padding-bottom: 10%;">
              <div v-if="!leftSelectedSubs || !leftSelectedSubs.length" class="py-3 text-center text-secondary">
                {{ $t("noData") }}
              </div>
              <div v-else>
                <div
                  v-for="(plan, index) in leftSelectedSubs"
                  :key="plan.id"
                  class="px-3 py-3 border rounded subscription-plan-item position-relative"
                  @click="moveRight(plan, index)"
                >
                  <subscription-plan-item :plan="plan" />
                </div>
              </div>
            </div>
          </div>
        </b-col>

        <!-- Cột phải: Danh sách gói cước đã chọn -->
        <b-col cols="6">
          <div style="height: 38px;" class="mb-3 d-flex align-items-center">
            <h5>{{ $t("selectedWithCount", { count: rightSelectedSubs ? rightSelectedSubs.length : 0 }) }}</h5>
          </div>
          <b-list-group class="contacts-list-group" style="max-height: 430px !important; overflow-y: auto;">
            <div v-if="!rightSelectedSubs || !rightSelectedSubs.length" class="py-3 text-center text-secondary">
              {{ $t("noData") }}
            </div>
            <b-list-group-item
              v-for="(sub, i) in rightSelectedSubs"
              :key="sub.id"
              button
              @click="moveLeft(sub, i)"
            >
              <subscription-plan-item :plan="sub" />
            </b-list-group-item>
          </b-list-group>
        </b-col>
      </b-row>

      <!-- Thanh công cụ điều khiển chân modal: Nút OK xác nhận và nút Hủy -->
      <div class="sticky" style="height: 40px; padding-top: 20px; text-align: right; border-top: 1px solid #dadada;">
        <b-button variant="primary" class="mr-3 floatRight" @click="done">{{ $t("ok") }}</b-button>
        <b-button variant="secondary" class="mr-3 floatRight" @click="onClickCancel">{{ $t("cancel") }}</b-button>
      </div>
    </div>
  </b-modal>
</template>

<script lang="ts">
import { Component, Prop } from "vue-property-decorator";
import { clone } from "lodash";
import BaseComponent from "@/components/BaseComponent";
import * as subscriptionPlanService from "@/service/subscriptionPlanService";
import SubscriptionPlanItem from "@/views/location/components/SubscriptionPlanItem.vue";

/**
 * Modal Transfer List dùng để gán gói cước cho khu vực.
 * Hỗ trợ:
 * - v-model hai chiều nhận danh sách gói cước đã chọn.
 * - Tìm kiếm in-memory phản hồi tức thì 0ms.
 * - Tự động tải ngầm danh sách gói cước khi mounted() để mở modal không bị chậm.
 * - Cơ chế sao lưu (backup) khôi phục dữ liệu an toàn nếu người dùng bấm Hủy.
 */
@Component({
  components: {
    SubscriptionPlanItem
  }
})
export default class SubscriptionPlanSelectionModal extends BaseComponent {
  /** Danh sách gói cước đã chọn truyền từ component cha (hỗ trợ v-model) */
  @Prop({ default: () => [] }) readonly value!: any[];
  /** ID định danh của modal */
  @Prop({ default: "sub-modal-location" }) readonly modalId!: string;

  /** Toàn bộ danh sách gói cước lấy từ API server */
  allPlans: any[] = [];
  /** Tổng số gói cước khả dụng */
  totalPackage = 0;
  /** Từ khóa tìm kiếm */
  searchKeyword = "";
  /** Danh sách gói cước chưa chọn hiển thị ở cột trái */
  leftSelectedSubs: any[] = [];
  /** Danh sách gói cước đã chọn hiển thị ở cột phải */
  rightSelectedSubs: any[] = [];
  /** Bản sao lưu danh sách trái trước khi người dùng thực hiện thao tác */
  backupLeft: any[] = [];
  /** Bản sao lưu danh sách phải trước khi người dùng thực hiện thao tác */
  backupRight: any[] = [];
  /** Cờ đánh dấu người dùng đã xác nhận lưu thay đổi qua nút OK */
  isConfirmed = false;

  /**
   * Lọc danh sách gói cước ở cột trái dựa theo từ khóa tìm kiếm (in-memory)
   * và tự động loại trừ những gói cước đã được chọn ở cột phải.
   */
  filterLeftPlans(keyword = "") {
    const searchLower = keyword?.trim().toLowerCase() || "";
    this.leftSelectedSubs = (this.allPlans || []).filter(item => {
      const isAlreadySelected = (this.rightSelectedSubs || []).some(
        data => (data.id != null && data.id === item.id) ||
                (data.subscriptionPlanId != null && data.subscriptionPlanId === item.id)
      );
      if (isAlreadySelected) return false;
      return !searchLower || item.name?.toLowerCase().includes(searchLower);
    });
    this.totalPackage = this.allPlans ? this.allPlans.length : 0;
  }

  /** Xử lý sự kiện khi người dùng gõ vào ô tìm kiếm */
  onSearchInput() {
    this.filterLeftPlans(this.searchKeyword);
  }

  /** Xử lý sự kiện khi người dùng bấm nút tìm kiếm hoặc nhấn Enter */
  searchSubscriptionPlans() {
    this.filterLeftPlans(this.searchKeyword);
  }

  /**
   * Tải toàn bộ danh sách gói cước từ server.
   * @param keyword Từ khóa lọc ban đầu
   * @param force Có bắt buộc tải lại bỏ qua cache hay không
   */
  async fetchSubscriptionPlans(keyword = "", force = false) {
    try {
      if (force || !this.allPlans || this.allPlans.length === 0) {
        this.allPlans = (await subscriptionPlanService.getPlans()) || [];
      }
      this.filterLeftPlans(keyword);
    } catch (e) {
      this.toastError(e);
    }
  }

  /** Bỏ chọn gói cước: Chuyển gói cước từ cột phải (đã chọn) sang cột trái (khả dụng) */
  moveLeft(plan: any, index: number) {
    this.leftSelectedSubs.push(this.rightSelectedSubs[index]);
    this.rightSelectedSubs.splice(index, 1);
  }

  /** Chọn gói cước: Chuyển gói cước từ cột trái (khả dụng) sang cột phải (đã chọn) */
  moveRight(plan: any, index: number) {
    this.rightSelectedSubs.push(plan);
    this.leftSelectedSubs.splice(index, 1);
  }

  /** Chọn tất cả gói cước */
  onClickSelectAll() {
    this.rightSelectedSubs = clone(this.allPlans) || [];
    this.leftSelectedSubs = [];
  }

  /** Bỏ chọn tất cả gói cước */
  onClickdeselectAll() {
    this.rightSelectedSubs = [];
    this.filterLeftPlans("");
  }

  /** Xác nhận lựa chọn: emit danh sách mới ra ngoài cha và đóng modal */
  done() {
    this.isConfirmed = true;
    this.$emit("input", clone(this.rightSelectedSubs));
    this.$emit("change", clone(this.rightSelectedSubs));
    this.backupLeft = [];
    this.backupRight = [];
    this.$bvModal.hide(this.modalId);
  }

  /** Hoàn tác dữ liệu về trạng thái trước khi mở modal nếu người dùng không bấm OK */
  cancelModalChanges() {
    this.rightSelectedSubs = clone(this.value) || [];
    this.leftSelectedSubs = clone(this.backupLeft) || [];
    this.backupLeft = [];
    this.backupRight = [];
  }

  /** Lắng nghe sự kiện modal bị ẩn (đóng bằng phím ESC hoặc backdrop) */
  onModalHide() {
    if (!this.isConfirmed) {
      this.cancelModalChanges();
    }
  }

  /** Xử lý khi bấm nút Hủy */
  onClickCancel() {
    this.isConfirmed = false;
    this.cancelModalChanges();
    this.$bvModal.hide(this.modalId);
  }

  /** Tải ngầm dữ liệu ngay khi component được khởi tạo để mở modal không bị trễ */
  async mounted() {
    await this.fetchSubscriptionPlans("");
  }

  /**
   * Mở modal chọn gói cước:
   * 1. Sao chép danh sách gói cước hiện tại của cha vào cột phải.
   * 2. Lọc cột trái tương ứng.
   * 3. Lưu bản sao lưu phục vụ hoàn tác.
   */
  async showModal() {
    this.isConfirmed = false;
    this.searchKeyword = "";
    this.rightSelectedSubs = clone(this.value) || [];

    if (!this.allPlans || this.allPlans.length === 0) {
      await this.fetchSubscriptionPlans("");
    } else {
      this.filterLeftPlans("");
    }

    this.backupLeft = clone(this.leftSelectedSubs);
    this.backupRight = clone(this.rightSelectedSubs);
    this.$bvModal.show(this.modalId);
  }
}
</script>

<style lang="scss">
.region-plan-modal-dialog {
  max-width: 80vw !important;
  margin: 1.75rem auto !important;
}
</style>

<style lang="scss" scoped>
.floatRight {
  float: right;
}

.close {
  display: none !important;
}

.subscription-plan-item {
  cursor: pointer;
  margin-bottom: 8px;
  transition: background-color 0.2s;

  &:hover {
    background-color: #f8f9fa;
  }
}
</style>
